<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Tables</h1>
          <p class="lede">Tap a table to open its order screen.</p>
        </div>
        <div class="head-actions">
          <button class="secondary" @click="load">Refresh</button>
          <button class="primary" @click="openNew" v-if="userRole === 'owner' || userRole === 'manager'">+ Add table</button>
        </div>
      </div>

      <div class="zones" v-if="tables.length">
        <div v-for="(list, zone) in tablesByZone" :key="zone" class="zone">
          <h3 class="zone-title" v-if="zone">{{ zone }}</h3>
          <div class="floor">
            <div v-for="t in list" :key="t.id" class="tile"
                 :class="{ occupied: t.open_order_id, empty: !t.open_order_id }"
                 @click="openTable(t)">
              <div class="tile-name">{{ t.name }}</div>
              <div class="tile-cap"><i class="mdi mdi-account-outline"></i> {{ t.capacity }}</div>
              <div v-if="t.open_order_id" class="tile-total">KES {{ format(t.open_total) }}</div>
              <div v-if="t.open_order_id" class="tile-status">Open · #{{ t.open_order_id }}</div>
              <div v-else class="tile-status muted">Available</div>
              <button v-if="userRole === 'owner' || userRole === 'manager'" class="tile-edit" @click.stop="openEdit(t)">
                <i class="mdi mdi-pencil"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="card muted center" style="padding:60px 24px">
        No tables yet. Click <strong>+ Add table</strong> to get started.
      </div>
    </div>

    <div v-if="showForm" class="modal-back" @click.self="showForm=false">
      <div class="modal card">
        <h3>{{ form.id ? 'Edit table' : 'Add table' }}</h3>
        <label>Name</label>
        <input v-model="form.name" placeholder="e.g. T1, Booth 3, Terrace 5" />
        <div class="row-2">
          <div>
            <label>Capacity (seats)</label>
            <input type="number" v-model.number="form.capacity" min="1" />
          </div>
          <div>
            <label>Zone (optional)</label>
            <input v-model="form.zone" placeholder="e.g. Terrace, Upstairs" />
          </div>
        </div>
        <div class="modal-actions">
          <button v-if="form.id" class="danger" @click="removeTable" style="margin-right:auto">Delete</button>
          <button class="secondary" @click="showForm=false">Cancel</button>
          <button class="primary" @click="save" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

export default {
  name: 'TablesFloor',
  components: { Navigation },
  data() {
    return {
      tables: [], userRole: sessionStorage.getItem('userRole') || '',
      showForm: false, saving: false, err: '',
      form: { id: 0, name: '', capacity: 4, zone: '' },
    };
  },
  computed: {
    tablesByZone() {
      const buckets = {};
      this.tables.forEach(t => {
        const z = t.zone || '';
        if (!buckets[z]) buckets[z] = [];
        buckets[z].push(t);
      });
      return buckets;
    }
  },
  async mounted() { await this.load(); },
  methods: {
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    async load() {
      const { data } = await execute({ function: 'listTables' }, paysol);
      if (data.success) this.tables = data.data;
    },
    async openTable(t) {
      // Open (or reuse) an order for this table, then navigate to waiter POS
      const { data } = await execute({ function: 'openTableOrder', tableId: t.id, guestCount: t.capacity }, paysol);
      if (!data.success) { alert(data.message); return; }
      this.$router.push('/waiter/' + data.data.id);
    },
    openNew() { this.form = { id: 0, name: '', capacity: 4, zone: '' }; this.err = ''; this.showForm = true; },
    openEdit(t) { this.form = { id: t.id, name: t.name, capacity: t.capacity, zone: t.zone || '' }; this.err = ''; this.showForm = true; },
    async save() {
      this.saving = true; this.err = '';
      try {
        const { data } = await execute({ function: 'saveTable', ...this.form }, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.showForm = false; await this.load();
      } finally { this.saving = false; }
    },
    async removeTable() {
      if (!confirm(`Delete table "${this.form.name}"?`)) return;
      const { data } = await execute({ function: 'deleteTable', id: this.form.id }, paysol);
      if (!data.success) { alert(data.message); return; }
      this.showForm = false; await this.load();
    },
  }
};
</script>

<style scoped>
.wrap { max-width: 1300px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; }
.head-actions { display: flex; gap: 8px; }
.zones { display: flex; flex-direction: column; gap: 20px; }
.zone-title { color: var(--paysol-muted); font-size: 13px; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 10px; }
.floor { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 14px; }
.tile {
  position: relative;
  background: var(--paysol-card); border: 2px solid var(--paysol-line);
  border-radius: 12px; padding: 18px 14px; cursor: pointer;
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  transition: transform .1s, border-color .15s, box-shadow .15s;
}
.tile:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0,0,0,.15); }
.tile.empty    { border-color: var(--paysol-line); }
.tile.empty:hover { border-color: var(--paysol-teal); }
.tile.occupied { border-color: var(--paysol-warning); background: rgba(245,158,11,.06); }
.tile.occupied:hover { border-color: var(--paysol-warning); }
.tile-name { color: var(--paysol-text); font-size: 22px; font-weight: 800; letter-spacing: -.01em; }
.tile-cap  { color: var(--paysol-muted); font-size: 12px; display: inline-flex; align-items: center; gap: 3px; }
.tile-total{ color: var(--paysol-teal); font-weight: 700; font-size: 15px; margin-top: 4px; }
.tile-status { color: var(--paysol-warning); font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }
.tile-status.muted { color: var(--paysol-muted); }
.tile-edit { position: absolute; top: 6px; right: 6px; background: transparent; border: none; color: var(--paysol-muted); padding: 4px; cursor: pointer; opacity: 0; }
.tile:hover .tile-edit { opacity: 1; }
.tile-edit:hover { color: var(--paysol-text); }
.muted { color: var(--paysol-muted); }
.center { text-align: center; }
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 460px; }
.row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
</style>
